import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { BarChart3, Download, ArrowUpRight, ArrowDownRight, Factory, GitCompare, Upload, FileSpreadsheet, X } from 'lucide-react';
import { API, formatCurrency } from '../../config';

const METRIC_OPTIONS = [
  { value: 'production', label: 'Production (MT)', isCurrency: false },
  { value: 'despatches', label: 'Despatches (MT)', isCurrency: false },
  { value: 'cost', label: 'Total Cost (₹)', isCurrency: true },
  { value: 'cost_per_mt', label: 'Cost / MT (₹/MT)', isCurrency: true },
];

const fmt = (v, isCurrency) => {
  if (v === null || v === undefined) return '—';
  if (isCurrency) return formatCurrency(v || 0);
  const n = Number(v || 0);
  if (n === 0) return '0';
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const fmtPct = (p) => (p === null || p === undefined ? null : `${p > 0 ? '+' : ''}${p}%`);

const buildFyYearOptions = () => {
  const today = new Date();
  const currentFyStart = today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1;
  return [currentFyStart - 2, currentFyStart - 1, currentFyStart, currentFyStart + 1];
};

// Tiny YoY delta chip (positive=green up, negative=red down) — used in YoY-compare mode under each value.
const DeltaChip = ({ pct, prev, isCurrency, deltaInvert }) => {
  if (pct === null || pct === undefined) {
    return <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">prev: {fmt(prev, isCurrency)}</div>;
  }
  const goodIsUp = !deltaInvert;
  const isUp = pct >= 0;
  const isGood = isUp === goodIsUp;
  const colorCls = isGood ? 'text-emerald-700 bg-emerald-50' : 'text-rose-700 bg-rose-50';
  const Icon = isUp ? ArrowUpRight : ArrowDownRight;
  return (
    <div className="flex items-center gap-1 mt-0.5 leading-tight">
      <span className={`inline-flex items-center gap-0.5 px-1 py-0 rounded text-[10px] font-semibold ${colorCls}`}>
        <Icon className="h-2.5 w-2.5" /> {fmtPct(pct)}
      </span>
      <span className="text-[10px] text-gray-400">vs {fmt(prev, isCurrency)}</span>
    </div>
  );
};

const CapacityBadge = ({ pct, target }) => {
  if (!target) return null;
  let colorCls = 'bg-amber-100 text-amber-800';
  if (pct >= 100) colorCls = 'bg-emerald-100 text-emerald-800';
  else if (pct < 70) colorCls = 'bg-rose-100 text-rose-800';
  return (
    <div className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${colorCls}`} title={`Target: ${fmt(target, false)} MT (prorated)`}>
      <Factory className="h-2.5 w-2.5" /> {pct}% of target
    </div>
  );
};

const FyMatrixView = ({ dashboardPlant, token }) => {
  const [metric, setMetric] = useState('production');
  const [fyStart, setFyStart] = useState(() => buildFyYearOptions()[2]);
  const [compare, setCompare] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importBusy, setImportBusy] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await axios.get(
        `${API}/fy-matrix/${fyStart}?metric=${metric}${compare ? '&compare=true' : ''}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load FY matrix');
      setData(null);
    } finally { setLoading(false); }
  }, [fyStart, metric, compare, token]);

  useEffect(() => { load(); }, [load]);

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/fy-matrix/${fyStart}/export.xlsx?metric=${metric}`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `fy_matrix_${fyStart}_${metric}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Failed to download Excel file'); }
  };

  const handleTemplateDownload = async () => {
    try {
      const res = await axios.get(`${API}/historical/template.xlsx`,
        { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'historical_monthly_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { alert('Failed to download template'); }
  };

  const handleHistoricalUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true);
    setImportResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await axios.post(`${API}/historical/import-monthly`, form,
        { headers: { Authorization: `Bearer ${token}` } });
      setImportResult(res.data);
      await load();
    } catch (err) {
      setImportResult({ error: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setImportBusy(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const isCurrency = METRIC_OPTIONS.find(m => m.value === metric)?.isCurrency;
  const deltaInvert = metric === 'cost' || metric === 'cost_per_mt'; // cost going down is good
  const singlePlantId = dashboardPlant && dashboardPlant.id !== 'all' ? dashboardPlant.id : null;
  const rowsToShow = data?.rows?.filter(r => !singlePlantId || r.plant_id === singlePlantId) || [];
  const showAvgPrdDayRow = metric === 'production' && !singlePlantId;

  return (
    <div className="space-y-4" data-testid="fy-matrix-view">
      {/* Header card */}
      <div className="bg-gradient-to-r from-[#156ACA] to-[#1e4d8c] rounded-lg shadow-lg p-4 sm:p-5 text-white">
        <div className="flex flex-wrap items-center gap-3 sm:gap-4">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <BarChart3 className="h-6 w-6 sm:h-7 sm:w-7 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-lg sm:text-xl font-bold">FY Production Matrix</h3>
              <p className="text-[11px] sm:text-xs text-blue-100">Indian FY (Apr→Mar) · Pro-rata averages</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 w-full md:w-auto md:ml-auto">
            <select
              value={fyStart}
              onChange={(e) => setFyStart(parseInt(e.target.value))}
              className="px-3 py-2 min-h-[40px] rounded-lg text-gray-900 bg-white text-sm font-medium flex-1 sm:flex-none"
              data-testid="fy-matrix-year-select"
            >
              {buildFyYearOptions().map((y) => (
                <option key={y} value={y}>FY {y}-{String(y + 1).slice(-2)}</option>
              ))}
            </select>
            <select
              value={metric}
              onChange={(e) => setMetric(e.target.value)}
              className="px-3 py-2 min-h-[40px] rounded-lg text-gray-900 bg-white text-sm font-medium flex-1 sm:flex-none"
              data-testid="fy-matrix-metric-select"
            >
              {METRIC_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setCompare(c => !c)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-lg text-sm font-semibold transition-colors ${compare ? 'bg-amber-400 text-amber-900' : 'bg-white/15 hover:bg-white/25 text-white'}`}
              data-testid="fy-matrix-compare-toggle"
            >
              <GitCompare className="h-4 w-4" />
              {compare ? `YoY` : 'YoY'}
            </button>
            <button
              type="button"
              onClick={() => setImportOpen(o => !o)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-lg text-sm font-semibold transition-colors ${importOpen ? 'bg-amber-400 text-amber-900' : 'bg-white/15 hover:bg-white/25 text-white'}`}
              data-testid="fy-matrix-import-toggle"
              title="Bulk-import prior-year monthly data"
            >
              <Upload className="h-4 w-4" />
              Import
            </button>
            <button
              type="button"
              onClick={handleDownload}
              className="inline-flex items-center gap-1.5 px-3 py-2 min-h-[40px] rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold shadow-sm"
              data-testid="fy-matrix-download-btn"
            >
              <Download className="h-4 w-4" />
              Excel
            </button>
          </div>
        </div>
        {singlePlantId && (
          <div className="mt-3 text-xs text-blue-100 bg-white/10 inline-block px-2 py-1 rounded">
            Filtered to a single plant — switch Plant to &quot;All Plants (Consolidated)&quot; for the full matrix.
          </div>
        )}
      </div>

      {/* Import History panel — slides open when "Import History" toggle is on */}
      {importOpen && (
        <div className="bg-white rounded-lg shadow-md border border-amber-200 p-5" data-testid="fy-matrix-import-panel">
          <div className="flex items-start gap-3 mb-4">
            <FileSpreadsheet className="h-6 w-6 text-amber-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-lg font-bold text-gray-900">Import Historical Monthly Data</h4>
              <p className="text-sm text-gray-600 mt-0.5">
                Bulk-load prior-year production for the YoY comparison. One row per Plant per (Year, Month).
                Each row is stored as a synthetic shift on the 15th of that month — re-uploads overwrite themselves.
                Months that already have real shift-level data are skipped (no clobber).
              </p>
            </div>
            <button onClick={() => { setImportOpen(false); setImportResult(null); }} className="text-gray-400 hover:text-gray-600" type="button">
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleTemplateDownload}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#156ACA] hover:bg-[#1e4d8c] text-white text-sm font-semibold"
              data-testid="fy-matrix-template-btn"
            >
              <Download className="h-4 w-4" />
              Download Template
            </button>
            <span className="text-sm text-gray-500">then</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              disabled={importBusy}
              onChange={handleHistoricalUpload}
              className="text-sm file:mr-3 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-amber-500 file:text-white file:font-semibold file:cursor-pointer hover:file:bg-amber-600"
              data-testid="fy-matrix-import-file"
            />
            {importBusy && <span className="text-sm text-amber-600 font-medium">Uploading...</span>}
          </div>

          {importResult && (
            <div className="mt-4 p-3 rounded-lg bg-gray-50 border border-gray-200" data-testid="fy-matrix-import-result">
              {importResult.error ? (
                <p className="text-sm text-rose-700 font-medium">{importResult.error}</p>
              ) : (
                <>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <span className="inline-flex items-center gap-1">
                      <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-semibold">{importResult.inserted}</span>
                      <span className="text-gray-600">inserted</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="px-2 py-0.5 rounded bg-sky-100 text-sky-800 font-semibold">{importResult.updated}</span>
                      <span className="text-gray-600">updated</span>
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold">{importResult.skipped || 0}</span>
                      <span className="text-gray-600">skipped</span>
                    </span>
                  </div>
                  {importResult.errors?.length > 0 && (
                    <ul className="mt-2 text-xs text-rose-700 space-y-1 max-h-32 overflow-y-auto">
                      {importResult.errors.map((e, i) => <li key={i}>• {e}</li>)}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      {loading && <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#156ACA]" /></div>}
      {error && !loading && <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center text-gray-700">{error}</div>}

      {!loading && !error && data && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm border-collapse" data-testid="fy-matrix-table">
              <thead className="bg-gradient-to-r from-[#1e4d8c] to-[#156ACA] text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-bold sticky left-0 bg-[#1e4d8c] z-20 text-xs uppercase tracking-wide border-r border-white/10">Plants</th>
                  {data.months.map((m, idx) => (
                    <th key={m} className="text-right px-3 py-3 font-bold text-xs uppercase tracking-wide border-r border-white/10 min-w-[78px]">{m}</th>
                  ))}
                  <th className="text-right px-3 py-3 font-bold text-xs uppercase tracking-wide bg-amber-400 text-amber-900 min-w-[100px]">TOTAL</th>
                  {metric === 'production' && (
                    <th className="text-right px-3 py-3 font-bold text-xs uppercase tracking-wide bg-orange-400 text-orange-900 min-w-[110px]">Highest PRD/Day</th>
                  )}
                  <th className="text-right px-3 py-3 font-bold text-xs uppercase tracking-wide bg-orange-300 text-orange-900 min-w-[120px]">
                    {metric === 'cost_per_mt' ? 'FY Avg ₹/MT' : `Avg ${metric === 'cost' ? '₹' : 'MT'} / Month`}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rowsToShow.length === 0 && (
                  <tr>
                    <td colSpan={data.months.length + 3} className="text-center text-gray-500 py-8">No data for this fiscal year yet.</td>
                  </tr>
                )}
                {rowsToShow.map((row, rowIdx) => (
                  <tr key={row.plant_id} className={rowIdx % 2 === 0 ? 'bg-white hover:bg-blue-50/50' : 'bg-gray-50/50 hover:bg-blue-50/50'}>
                    <td className={`text-left px-4 py-2 font-semibold text-gray-800 sticky left-0 z-10 border-r border-gray-200 ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>{row.plant_name}</td>
                    {row.months.map((v, idx) => (
                      <td key={idx} className="text-right px-3 py-2 text-gray-800 border-r border-gray-100 align-top">
                        <div className="font-medium tabular-nums">{fmt(v, isCurrency)}</div>
                        {compare && row.prev_months && (
                          <DeltaChip pct={row.yoy_months_pct?.[idx]} prev={row.prev_months[idx]} isCurrency={isCurrency} deltaInvert={deltaInvert} />
                        )}
                      </td>
                    ))}
                    <td className="text-right px-3 py-2 font-bold text-gray-900 bg-amber-50 border-r border-amber-200 align-top">
                      <div className="tabular-nums">{fmt(row.total, isCurrency)}</div>
                      {compare && <DeltaChip pct={row.yoy_total_pct} prev={row.prev_total} isCurrency={isCurrency} deltaInvert={deltaInvert} />}
                      {metric === 'production' && <CapacityBadge pct={row.capacity_pct} target={row.capacity_target_mt} />}
                    </td>
                    {metric === 'production' && (
                      <td className="text-right px-3 py-2 font-bold text-gray-900 bg-orange-50 border-r border-orange-200 align-top" title={row.highest_day_label || ''}>
                        <div className="tabular-nums">{fmt(row.highest_day_value, false)}</div>
                        {row.highest_day_label && <div className="text-[10px] text-gray-500 mt-0.5">{row.highest_day_label}</div>}
                        {compare && <DeltaChip pct={row.yoy_highest_day_pct} prev={row.prev_highest_day_value} isCurrency={false} deltaInvert={false} />}
                      </td>
                    )}
                    <td className="text-right px-3 py-2 font-bold text-gray-900 bg-orange-50 align-top">
                      <div className="tabular-nums">{fmt(row.avg_per_month, isCurrency)}</div>
                      {compare && <DeltaChip pct={row.yoy_avg_per_month_pct} prev={row.prev_avg_per_month} isCurrency={isCurrency} deltaInvert={deltaInvert} />}
                    </td>
                  </tr>
                ))}
              </tbody>
              {!singlePlantId && rowsToShow.length > 0 && (
                <tfoot>
                  {/* TOTAL row */}
                  <tr className="bg-amber-200">
                    <td className="text-left px-4 py-2.5 font-extrabold text-amber-900 sticky left-0 bg-amber-200 z-10 border-r border-amber-300 uppercase text-xs tracking-wide">TOTAL</td>
                    {data.footer.monthly_totals.map((v, idx) => (
                      <td key={idx} className="text-right px-3 py-2.5 font-bold text-amber-900 border-r border-amber-300 align-top">
                        <div className="tabular-nums">{fmt(v, isCurrency)}</div>
                        {compare && data.footer.prev_monthly_totals && (
                          <DeltaChip pct={data.footer.yoy_monthly_totals_pct?.[idx]} prev={data.footer.prev_monthly_totals[idx]} isCurrency={isCurrency} deltaInvert={deltaInvert} />
                        )}
                      </td>
                    ))}
                    <td className="text-right px-3 py-2.5 font-extrabold text-amber-900 border-r border-amber-300 align-top">
                      <div className="tabular-nums">{fmt(data.footer.grand_total, isCurrency)}</div>
                      {compare && <DeltaChip pct={data.footer.yoy_grand_total_pct} prev={data.footer.prev_grand_total} isCurrency={isCurrency} deltaInvert={deltaInvert} />}
                      {metric === 'production' && <CapacityBadge pct={data.footer.capacity_pct} target={data.footer.capacity_target_mt} />}
                    </td>
                    {metric === 'production' && (
                      <td className="text-right px-3 py-2.5 font-extrabold text-amber-900 bg-sky-200 border-r border-sky-300 align-top" title={data.footer.highest_day_label || ''}>
                        <div className="tabular-nums">{fmt(data.footer.highest_day_value, false)}</div>
                        {data.footer.highest_day_label && <div className="text-[10px] text-sky-800 mt-0.5">{data.footer.highest_day_label}</div>}
                        {compare && <DeltaChip pct={data.footer.yoy_highest_day_pct} prev={data.footer.prev_highest_day_value} isCurrency={false} deltaInvert={false} />}
                      </td>
                    )}
                    <td className="text-right px-3 py-2.5 font-extrabold text-amber-900 align-top">
                      <div className="tabular-nums">{fmt(data.footer.avg_per_month, isCurrency)}</div>
                      {compare && <DeltaChip pct={data.footer.yoy_avg_per_month_pct} prev={data.footer.prev_avg_per_month} isCurrency={isCurrency} deltaInvert={deltaInvert} />}
                    </td>
                  </tr>
                  {/* AVG PRD/DAY row (production only) */}
                  {showAvgPrdDayRow && (
                    <tr className="bg-emerald-200">
                      <td className="text-left px-4 py-2.5 font-extrabold text-emerald-900 sticky left-0 bg-emerald-200 z-10 border-r border-emerald-300 uppercase text-xs tracking-wide">AVG PRD / Day (MT)</td>
                      {data.footer.avg_prd_per_day_per_month.map((v, idx) => (
                        <td key={idx} className="text-right px-3 py-2.5 font-bold text-emerald-900 border-r border-emerald-300 align-top">
                          <div className="tabular-nums">{fmt(v, false)}</div>
                          {compare && data.footer.prev_avg_prd_per_day_per_month && (
                            <DeltaChip pct={data.footer.yoy_avg_prd_per_day_per_month_pct?.[idx]} prev={data.footer.prev_avg_prd_per_day_per_month[idx]} isCurrency={false} deltaInvert={false} />
                          )}
                        </td>
                      ))}
                      <td className="text-right px-3 py-2.5 font-extrabold text-emerald-900 border-r border-emerald-300 align-top">
                        <div className="tabular-nums">{fmt(data.footer.avg_prd_per_day_total, false)}</div>
                        {compare && <DeltaChip pct={data.footer.yoy_avg_prd_per_day_total_pct} prev={data.footer.prev_avg_prd_per_day_total} isCurrency={false} deltaInvert={false} />}
                      </td>
                      <td className="text-right px-3 py-2.5 text-gray-400 bg-gray-100 border-r border-gray-200">—</td>
                      <td className="text-right px-3 py-2.5 font-extrabold text-emerald-900">
                        <div className="tabular-nums">{fmt(data.footer.avg_prd_per_day_total, false)}</div>
                      </td>
                    </tr>
                  )}
                </tfoot>
              )}
            </table>
          </div>
          <div className="bg-gray-50 px-4 py-3 text-xs text-gray-600 flex flex-wrap items-center gap-x-6 gap-y-1 border-t border-gray-200">
            <span><span className="font-semibold">{data.fy_label}</span> · Calendar pro-rata averages</span>
            {compare && data.prev_fy_label && <span>YoY = current vs same period of <span className="font-semibold">{data.prev_fy_label}</span></span>}
            {metric === 'production' && <span>Capacity badge = % of plant&apos;s pro-rated annual target</span>}
            <span>Future months show 0 until data is entered</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FyMatrixView;
